import { useUpdateCandidate, type Candidate } from '@entities/candidates';
import { useGetEducationLevels } from '@entities/educationLevel';
import { Button, Flex, Loader, Title } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { QueryKey } from '@shared/config';
import {
  Form,
  FormPhoneInput,
  FormSelect,
  FormTextField,
  FormTextarea,
} from '@shared/ui';
import { showNotification } from '@shared/utils';
import { useQueryClient } from '@tanstack/react-query';
import { IconChevronDown } from '@tabler/icons-react';
import { useParams } from 'react-router';
import { CandidateCitySelect } from '@widgets/candidate/candidate-city-select';
import { $CandidatePrivateDataEditSchema } from '../schema';
import type { CandidatePrivateDataEditInitialValues } from '../types';

type CandidatePrivateDataFormProps = {
  age: number | null;
  initialValues: CandidatePrivateDataEditInitialValues;
  interviewRequired: boolean;
  onCancel: () => void;
};

export const CandidatePrivateDataForm = ({
  age,
  initialValues,
  interviewRequired,
  onCancel,
}: CandidatePrivateDataFormProps) => {
  const queryClient = useQueryClient();
  const { id } = useParams();
  const { mutate: updateCandidate, isPending } = useUpdateCandidate(id ?? '');
  const { data: educationLevels, isLoading: areEducationLevelsLoading } =
    useGetEducationLevels();
  const form = useForm({
    initialValues,
    validate: zodResolver($CandidatePrivateDataEditSchema),
  });

  const handleSubmit = (values: CandidatePrivateDataEditInitialValues) => {
    updateCandidate(
      {
        phone: values.phone,
        firstName: values.firstName || null,
        lastName: values.lastName || null,
        age,
        cityIds: values.cityIds,
        allCities: values.allCities,
        educationLevel: {
          id: values.educationLevelId ? Number(values.educationLevelId) : null,
          professionName: values.professionName || null,
        },
        preferredCommunicationLanguage:
          values.preferredCommunicationLanguage || null,
        generalInfo: values.generalInfo || null,
        interviewRequired,
      },
      {
        onSuccess: data => {
          queryClient.setQueryData(
            [QueryKey.GET_CANDIDATE, id],
            (previous: Candidate) => ({ ...previous, ...data })
          );
          onCancel();
        },
        onError: error => {
          showNotification({
            error,
            variant: 'error',
            message:
              error.response?.data.message ||
              'Не удалось обновить данные кандидата',
            color: 'red',
          });
        },
      }
    );
  };

  return (
    <Form onSubmit={form.onSubmit(handleSubmit)}>
      <Flex gap={24} direction='column'>
        <Title order={4}>Основная информация</Title>

        <Flex direction='column' gap={16}>
          <FormTextField
            title='Фамилия'
            description='Фамилия кандидата'
            placeholder='Введите фамилию'
            containerProps={{ pt: 0 }}
            key={form.key('lastName')}
            {...form.getInputProps('lastName')}
          />
          <FormTextField
            title='Имя'
            description='Имя кандидата'
            placeholder='Введите имя'
            containerProps={{ pt: 0 }}
            key={form.key('firstName')}
            {...form.getInputProps('firstName')}
          />
          <FormTextField
            title='Возраст'
            description='Возраст кандидата'
            placeholder='-'
            containerProps={{ pt: 0 }}
            value={age?.toString() ?? ''}
            readOnly
            disabled
          />
          <FormPhoneInput
            title='Контактный телефон'
            description='Номер телефона для связи'
            placeholder='Введите номер'
            containerProps={{ pt: 0 }}
            key={form.key('phone')}
            {...form.getInputProps('phone')}
          />
          <FormTextField
            title='Язык общения'
            description='Предпочтительный язык общения'
            placeholder='-'
            containerProps={{ pt: 0 }}
            key={form.key('preferredCommunicationLanguage')}
            {...form.getInputProps('preferredCommunicationLanguage')}
          />
          <CandidateCitySelect
            mode='edit'
            allCities={form.values.allCities}
            cityIds={form.values.cityIds}
            onChange={value => {
              form.setFieldValue('allCities', value.allCities);
              form.setFieldValue('cityIds', value.cityIds);
            }}
          />
          <FormSelect
            title='Уровень образования'
            description='Уровень образования кандидата'
            placeholder='Выберите уровень образования'
            searchable
            clearable
            disabled={areEducationLevelsLoading}
            data={educationLevels?.data.map(level => ({
              label: level.name,
              value: level.id.toString(),
            }))}
            rightSection={
              areEducationLevelsLoading ? (
                <Loader size='xs' />
              ) : (
                <IconChevronDown
                  size={20}
                  color='var(--mantine-color-primary-7)'
                />
              )
            }
            containerProps={{ pt: 0 }}
            key={form.key('educationLevelId')}
            {...form.getInputProps('educationLevelId')}
          />
          <FormTextField
            title='Специальность'
            description='Специальность кандидата'
            placeholder='Введите специальность'
            maxLength={255}
            containerProps={{ pt: 0 }}
            key={form.key('professionName')}
            {...form.getInputProps('professionName')}
          />
          <FormTextarea
            title='Общая информация'
            description='Общая информация о кандидате'
            placeholder='Введите общую информацию'
            containerProps={{ pt: 0 }}
            key={form.key('generalInfo')}
            {...form.getInputProps('generalInfo')}
          />
        </Flex>

        <Flex gap={8} mt={16}>
          <Button variant='gray' onClick={onCancel}>
            Назад
          </Button>
          <Button type='submit' loading={isPending} disabled={isPending}>
            Сохранить
          </Button>
        </Flex>
      </Flex>
    </Form>
  );
};
